import { isDevMode } from '@angular/core';
import { Route } from '@angular/router';
import { authGuard, notAuthGuard } from './common/guards/auth.guard';
import { estimationConfigurationGuard } from './common/guards/estimation-configuration.guard';
import { opportunityEditGuard } from './common/guards/opportunity-edit.guard';
import { MainLayoutComponent } from './layout/main/main-layout';
import { Path } from './common/constants';
export const appRoutes: Route[] = [
  {
    path: '',
    redirectTo: Path.DASHBOARD,
    pathMatch: 'full',
  },
  {
    path: Path.LOGIN,
    loadComponent: () =>
      import('./pages/login/login').then(m => m.LoginComponent),
    canActivate: [notAuthGuard],
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: Path.DASHBOARD,
        loadComponent: () =>
          import('./pages/dashboard/dashboard').then(m => m.DashboardComponent),
      },
      ...(isDevMode()
        ? [
            {
              path: Path.UI_PLAYGROUND,
              loadComponent: () =>
                import('./pages/ui-playground/ui-playground').then(
                  m => m.UiPlaygroundComponent
                ),
            },
          ]
        : []),
      {
        path: Path.USERS,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/users/users').then(m => m.UsersComponent),
          },
          {
            path: Path.USERS_CREATE,
            loadComponent: () =>
              import('./pages/users/user-form/user-form').then(
                m => m.UserFormComponent
              ),
          },
          {
            path: `${Path.USERS_EDIT}/:id`,
            loadComponent: () =>
              import('./pages/users/user-form/user-form').then(
                m => m.UserFormComponent
              ),
          },
          {
            path: `${Path.USERS_VIEW}/:id`,
            loadComponent: () =>
              import('./pages/users/view-user/view-user').then(
                m => m.ViewUserComponent
              ),
          },
        ],
      },
      {
        path: Path.SYNC_CONFIG,
        loadComponent: () =>
          import('./pages/sync-configuration/sync-configuration').then(
            m => m.SyncConfigurationComponent
          ),
      },
      {
        path: Path.SYNCHRONIZATION,
        loadComponent: () =>
          import('./pages/synchronization/synchronization').then(
            m => m.SynchronizationComponent
          ),
      },
      {
        path: Path.ORGANIZATION,
        loadComponent: () =>
          import('./pages/organization-chart/organization-chart').then(
            m => m.OrganizationChartComponent
          ),
      },
      {
        path: Path.PROJECTS,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/projects/projects').then(
                m => m.ProjectsComponent
              ),
          },
          {
            path: Path.PROJECT_CREATE,
            loadComponent: () =>
              import('./pages/projects/project-form/project-form').then(
                m => m.ProjectFormComponent
              ),
          },
          {
            path: `${Path.PROJECT_EDIT}/:id`,
            loadComponent: () =>
              import('./pages/projects/project-form/project-form').then(
                m => m.ProjectFormComponent
              ),
          },
          {
            path: `${Path.PROJECT_VIEW}/:project_id/contracts/create`,
            loadComponent: () =>
              import(
                './pages/projects/project-view/contracts/contract-form/contract-form'
              ).then(m => m.ContractFormComponent),
          },
          {
            path: `${Path.PROJECT_VIEW}/:project_id/contracts/edit/:id`,
            loadComponent: () =>
              import(
                './pages/projects/project-view/contracts/contract-form/contract-form'
              ).then(m => m.ContractFormComponent),
          },
          {
            path: `${Path.PROJECT_VIEW}/:id`,
            loadComponent: () =>
              import('./pages/projects/project-view/project-view').then(
                m => m.ProjectViewComponent
              ),
          },
        ],
      },
      {
        path: Path.ACCOUNTS,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/accounts/accounts').then(
                m => m.AccountsComponent
              ),
          },
          {
            path: `${Path.CONTACTS}/:accountId/:accountName`,
            loadComponent: () =>
              import('./pages/contacts/contacts').then(
                m => m.ContactsComponent
              ),
          },
        ],
      },
      {
        path: Path.PROJECT_SETTINGS,
        loadComponent: () =>
          import('./pages/project-settings/project-settings').then(
            m => m.ProjectSettingsComponent
          ),
      },
      {
        path: Path.CONTRACTS,
        children: [
          {
            path: `${Path.CONTRACT_VIEW}/:project_id/:id`,
            loadComponent: () =>
              import(
                './pages/projects/project-view/contracts/contract-view/contract-view'
              ).then(m => m.ContractViewComponent),
          },
          {
            path: `${Path.CONTRACT_VIEW}/:project_id/:id/invoices/create`,
            loadComponent: () =>
              import(
                './pages/projects/project-view/contracts/contract-view/invoices/invoice-form/invoice-form'
              ).then(m => m.InvoiceFormComponent),
          },
          {
            path: `${Path.CONTRACT_VIEW}/:project_id/:id/invoices/view/:invoiceId`,
            loadComponent: () =>
              import(
                './pages/projects/project-view/contracts/contract-view/invoices/invoice-view/invoice-view'
              ).then(m => m.InvoiceViewComponent),
          },
          {
            path: `${Path.CONTRACT_VIEW}/:project_id/:id/invoices/edit/:invoiceId`,
            loadComponent: () =>
              import(
                './pages/projects/project-view/contracts/contract-view/invoices/invoice-form/invoice-form'
              ).then(m => m.InvoiceFormComponent),
          },
        ],
      },
      {
        path: Path.CUSTOM_VARIABLES,
        loadComponent: () =>
          import('./pages/custom-variables/custom-variables').then(
            m => m.CustomVariablesComponent
          ),
      },
      {
        path: Path.REPORTS,
        children: [
          {
            path: Path.SYNC_REPORTS_PAGE,
            loadComponent: () =>
              import('./pages/reports/sync-reports/sync-reports-overview').then(
                m => m.SyncReportsOverviewComponent
              ),
          },
          {
            path: Path.FIXED_REPORTS,
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/reports/fixed-reports/fixed-reports').then(
                    m => m.FixedReportsComponent
                  ),
              },
              {
                path: Path.PER_EMPLOYEE_PER_PROJECT,
                loadComponent: () =>
                  import(
                    './pages/reports/fixed-reports/per-employee-per-project/per-employee-per-project'
                  ).then(m => m.PerEmployeePerProjectComponent),
              },
              {
                path: Path.PER_EMPLOYEE_PER_PERIOD,
                loadComponent: () =>
                  import(
                    './pages/reports/fixed-reports/per-employee-per-period/per-employee-per-period'
                  ).then(m => m.PerEmployeePeriodComponent),
              },
              {
                path: Path.PER_PROJECT_PER_PERIOD,
                loadComponent: () =>
                  import(
                    './pages/reports/fixed-reports/per-project-per-period/per-project-per-period'
                  ).then(m => m.PerProjectPeriodComponent),
              },
            ],
          },
        ],
      },
      {
        path: Path.SYNC_REPORTS,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/reports/sync-reports/sync-reports-overview').then(
                m => m.SyncReportsOverviewComponent
              ),
          },
          {
            path: Path.SYNC_REPORTS_AZURE,
            loadComponent: () =>
              import('./pages/reports/sync-reports/azure/azure-report').then(
                m => m.AzureReportComponent
              ),
          },
          {
            path: Path.SYNC_REPORTS_ZOHO,
            loadComponent: () =>
              import('./pages/reports/sync-reports/zoho/zoho-report').then(
                m => m.ZohoReportComponent
              ),
          },
        ],
      },
      {
        path: Path.OPPORTUNITIES,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/opportunities/opportunities').then(
                m => m.OpportunitiesComponent
              ),
          },
          {
            path: Path.OPPORTUNITY_NEW,
            loadComponent: () =>
              import(
                './pages/opportunities/opportunity-form/opportunity-form'
              ).then(m => m.OpportunityFormComponent),
          },
          {
            path: `${Path.OPPORTUNITY_EDIT}/:id`,
            loadComponent: () =>
              import(
                './pages/opportunities/opportunity-form/opportunity-form'
              ).then(m => m.OpportunityFormComponent),
            canActivate: [opportunityEditGuard],
          },
          {
            path: `${Path.OPPORTUNITY_VIEW}/:id`,
            loadComponent: () =>
              import(
                './pages/opportunities/opportunity-view/opportunity-view'
              ).then(m => m.OpportunityViewComponent),
          },
          {
            path: `:id/${Path.OPPORTUNITY_SERVICES}`,
            loadComponent: () =>
              import(
                './pages/opportunities/opportunity-form/opportunity-form'
              ).then(m => m.OpportunityFormComponent),
          },
          {
            path: `:opportunityId/service-details`,
            loadChildren: () =>
              import(
                './pages/opportunities/service-details/service-details.routes'
              ).then(m => m.SERVICE_DETAILS_ROUTES),
          },
          {
            path: `:opportunityId/${Path.ESTIMATION}/:serviceKey/${Path.ESTIMATION_NEW}`,
            loadComponent: () =>
              import(
                './pages/opportunities/estimation-form/estimation-form'
              ).then(m => m.EstimationFormComponent),
          },
          {
            path: `:opportunityId/${Path.ESTIMATION}/:serviceKey/:estimationId/${Path.ESTIMATION_EDIT}`,
            loadComponent: () =>
              import(
                './pages/opportunities/estimation-form/estimation-form'
              ).then(m => m.EstimationFormComponent),
          },
          {
            path: `:opportunityId/${Path.ESTIMATION}/:serviceKey/:estimationId`,
            loadComponent: () =>
              import(
                './pages/opportunities/estimation-view/estimation-view'
              ).then(m => m.EstimationViewComponent),
          },
          {
            path: `:opportunityId/${Path.GENERATE_PROPOSAL}`,
            loadComponent: () =>
              import(
                './pages/opportunities/generate-proposal/generate-proposal'
              ).then(m => m.GenerateProposalComponent),
          },
        ],
      },
      {
        path: Path.RESOURCE_MANAGEMENT_OVERVIEW,
        loadComponent: () =>
          import('./pages/resource-management/overview/overview').then(
            m => m.ResourceManagementOverviewComponent
          ),
      },
      {
        path: Path.RESOURCE_MANAGEMENT_TIMESHEET,
        loadChildren: () =>
          import('./pages/resource-management/timesheet/timesheet.routes').then(
            m => m.TIMESHEET_ROUTES
          ),
      },
      {
        path: Path.RESOURCE_MANAGEMENT_ESTIMATIONS,
        loadChildren: () =>
          import(
            './pages/resource-management/estimations/estimations.routes'
          ).then(m => m.ESTIMATIONS_ROUTES),
      },
      {
        // The same screens again, listing and creating percentage estimations.
        path: Path.RESOURCE_MANAGEMENT_PERCENTAGE_ESTIMATIONS,
        loadChildren: () =>
          import(
            './pages/resource-management/estimations/estimations.routes'
          ).then(m => m.PERCENTAGE_ESTIMATIONS_ROUTES),
      },
      {
        path: Path.DOCUMENT_CENTRE,
        loadComponent: () =>
          import(
            './pages/contracts-document-centre/contracts-document-centre'
          ).then(m => m.ContractsDocumentCentreComponent),
      },
      {
        path: Path.RESOURCE_MANAGEMENT_JOB_TITLES,
        canActivate: [estimationConfigurationGuard],
        loadComponent: () =>
          import('./pages/resource-management/job-titles/job-titles').then(
            m => m.JobTitlesComponent
          ),
      },
      {
        path: Path.RESOURCE_MANAGEMENT_SETTINGS,
        canActivate: [estimationConfigurationGuard],
        loadComponent: () =>
          import('./pages/resource-management/settings/settings').then(
            m => m.ResourceManagementSettingsComponent
          ),
      },
      {
        path: '**',
        redirectTo: Path.DASHBOARD,
      },
    ],
  },
];
